// Platform detection — Capacitor, viewport breakpoints, mobile flags.
// Импортировать только отсюда. Прямые window.innerWidth и process.* запрещены.
import { useEffect, useState, useSyncExternalStore } from "react";

// __IS_CAPACITOR__ / __FORCE_MOBILE__ inj через Vite define (vite.config.ts).
export const IS_CAPACITOR: boolean = typeof __IS_CAPACITOR__ !== "undefined" && __IS_CAPACITOR__;
export const FORCE_MOBILE: boolean = typeof __FORCE_MOBILE__ !== "undefined" && __FORCE_MOBILE__;

/**
 * Брейкпоинты приложения. Экспортируются ради геометрии оболочки
 * (`components/layout/_panel-geometry.ts`): она сравнивает с ними ИЗМЕРЕННУЮ
 * ширину окна, а не спрашивает matchMedia — оба ответа должны приходить из
 * одного кадра, иначе на ресайзе панели разъезжаются с CSS-раскладкой.
 */
export const BREAKPOINTS = {
	sm: 640,
	md: 768,
	lg: 1024,
	xl: 1280,
} as const;
export type Breakpoint = keyof typeof BREAKPOINTS;

const isBrowser = typeof window !== "undefined";

function readViewport(): { width: number; height: number } {
	if (!isBrowser) return { width: 1280, height: 800 };
	return { width: window.innerWidth, height: window.innerHeight };
}

// Ширина окна как реактивное значение — ОДИН слушатель `resize` на вкладку.
//
// Отдельно от `useViewport()`: геометрию панелей считают три компонента
// (оболочка, панель репетитора, панель уведомлений), и им нужна одна и та же
// ширина в одном кадре. `useViewport` дал бы три независимых `useState` и
// перерисовку ещё и на смену высоты (экранная клавиатура), к ширине отношения
// не имеющую.
const widthListeners = new Set<() => void>();
let currentWidth = isBrowser ? window.innerWidth : 1280;
let widthAttached = false;

function handleWidthResize(): void {
	if (window.innerWidth === currentWidth) return;
	currentWidth = window.innerWidth;
	for (const listener of widthListeners) listener();
}

function subscribeWidth(listener: () => void): () => void {
	widthListeners.add(listener);
	if (!widthAttached && isBrowser) {
		widthAttached = true;
		window.addEventListener("resize", handleWidthResize, { passive: true });
	}
	return () => {
		widthListeners.delete(listener);
		if (widthListeners.size === 0 && widthAttached) {
			widthAttached = false;
			window.removeEventListener("resize", handleWidthResize);
		}
	};
}

/** Текущая ширина окна в px (реактивно на `resize`). */
export function useViewportWidth(): number {
	return useSyncExternalStore(
		subscribeWidth,
		() => {
			// Пока подписчиков нет, слушателя тоже нет — снимок обязан читать окно
			// напрямую, иначе вернём протухшее число.
			if (!widthAttached && isBrowser) currentWidth = window.innerWidth;
			return currentWidth;
		},
		() => 1280,
	);
}

export function useViewport(): { width: number; height: number } {
	const [size, setSize] = useState(readViewport);
	useEffect(() => {
		if (!isBrowser) return;
		const onResize = (): void => setSize(readViewport());
		window.addEventListener("resize", onResize, { passive: true });
		return () => window.removeEventListener("resize", onResize);
	}, []);
	return size;
}

function useMediaQuery(query: string): boolean {
	const [matches, setMatches] = useState<boolean>(() => {
		if (!isBrowser) return false;
		return window.matchMedia(query).matches;
	});
	useEffect(() => {
		if (!isBrowser) return;
		const mql = window.matchMedia(query);
		const handler = (e: MediaQueryListEvent): void => setMatches(e.matches);
		mql.addEventListener("change", handler);
		setMatches(mql.matches);
		return () => mql.removeEventListener("change", handler);
	}, [query]);
	return matches;
}

export function useIsLgUp(): boolean {
	return useMediaQuery(`(min-width: ${BREAKPOINTS.lg}px)`);
}

export function useIsMdUp(): boolean {
	return useMediaQuery(`(min-width: ${BREAKPOINTS.md}px)`);
}

export function useIsSmUp(): boolean {
	return useMediaQuery(`(min-width: ${BREAKPOINTS.sm}px)`);
}

/**
 * Coarse-pointer (touch primary) реактивно. Используется гейтами табличного UX:
 * floating-меню только на fine-pointer (desktop), на touch — TableMobileBar;
 * «+»/drag/resize-ручка отключаются на coarse. Гибриды (Android+мышь, iPad+
 * trackpad) в touch-primary режиме дают coarse → корректно ведут себя как touch.
 */
export function useIsCoarsePointer(): boolean {
	return useMediaQuery("(pointer: coarse)");
}

/** Fine-pointer (mouse/trackpad primary) — BubbleMenu, DragHandle и т.п. */
export function useIsFinePointer(): boolean {
	return useMediaQuery("(pointer: fine)");
}

/**
 * Является ли текущий контекст mobile-платформой:
 *  - Capacitor → всегда true
 *  - Web → viewport < sm (640px)
 */
export function useIsMobilePlatform(): boolean {
	const isSmUp = useIsSmUp();
	if (IS_CAPACITOR || FORCE_MOBILE) return true;
	return !isSmUp;
}

/**
 * Статический флаг (без подписки на resize). Использовать только в инициализации
 * (например, для выбора стартового layout). Для реактивности — useIsMobilePlatform.
 */
export const IS_MOBILE_PLATFORM: boolean = (() => {
	if (IS_CAPACITOR || FORCE_MOBILE) return true;
	if (!isBrowser) return false;
	return window.innerWidth < BREAKPOINTS.sm;
})();
